/** GeoJSON helpers for plot location on the officer map. */

export type LatLng = { lat: number; lng: number };

type GeoJsonLike = {
  type?: string;
  coordinates?: unknown;
  geometry?: GeoJsonLike;
};

/** Parse boundary values from API/DB (object, JSON string, or double-encoded string). */
export function normalizeBoundaryGeojson(raw: unknown): Record<string, unknown> | null {
  let value: unknown = raw;
  for (let depth = 0; depth < 3; depth++) {
    if (value == null) return null;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return null;
      try {
        value = JSON.parse(trimmed);
      } catch {
        return null;
      }
      continue;
    }
    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      if (obj['type'] === 'Feature' && obj['geometry']) {
        value = obj['geometry'];
        continue;
      }
      if (obj['coordinates'] && !obj['type']) {
        return { type: 'Polygon', coordinates: obj['coordinates'] };
      }
      if (typeof obj['type'] === 'string') {
        return { ...obj, type: obj['type'] };
      }
      return obj;
    }
    return null;
  }
  return null;
}

function firstRingFromNormalized(geojson: unknown): number[][] | null {
  const geo = normalizeBoundaryGeojson(geojson) as GeoJsonLike | null;
  return firstRing(geo);
}

/**
 * Extract a WGS84 polygon ring as [lat, lng] pairs for Leaflet.
 * Returns null when missing, malformed, or local-metre coordinates.
 */
export function extractLatLngRing(geojson: unknown): LatLng[] | null {
  const ring = firstRing(geojson as GeoJsonLike | null);
  if (!ring?.length) return null;
  if (looksLikeLocalMetres(ring)) return null;

  const out: LatLng[] = [];
  for (const pair of ring) {
    const lng = Number(pair[0]);
    const lat = Number(pair[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) continue;
    out.push({ lat, lng });
  }
  return out.length >= 3 ? out : null;
}

/**
 * Extract a representative WGS84 point from plan/measurement footprint GeoJSON.
 * Returns null when missing, malformed, or not usable on OpenStreetMap (e.g. local metres).
 */
export function extractMapPoint(geojson: unknown): LatLng | null {
  const ring = extractLatLngRing(geojson);
  if (ring?.length) {
    const sum = ring.reduce(
      (acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }),
      { lat: 0, lng: 0 }
    );
    return { lat: sum.lat / ring.length, lng: sum.lng / ring.length };
  }

  // Point geometries: fall back to single-coordinate centroid logic
  const raw = firstRing(geojson as GeoJsonLike | null);
  if (!raw?.length || looksLikeLocalMetres(raw)) return null;
  const lng = Number(raw[0][0]);
  const lat = Number(raw[0][1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
}

function firstRing(geo: GeoJsonLike | null | undefined): number[][] | null {
  if (!geo) return null;
  if (geo.type === 'Feature' && geo.geometry) return firstRing(geo.geometry);
  if (geo.type === 'FeatureCollection') return null;

  const type = (geo.type || '').toLowerCase();
  const coords = geo.coordinates;
  if (!coords) return null;

  if (type === 'point' && Array.isArray(coords) && coords.length >= 2) {
    return [[Number(coords[0]), Number(coords[1])]];
  }
  if (type === 'polygon' && Array.isArray(coords) && Array.isArray(coords[0])) {
    return coords[0] as number[][];
  }
  if (
    type === 'multipolygon' &&
    Array.isArray(coords) &&
    Array.isArray(coords[0]) &&
    Array.isArray(coords[0][0])
  ) {
    return coords[0][0] as number[][];
  }
  return null;
}

/** India WGS84 bounding box (Prayagraj / UP). */
const INDIA_LNG_MIN = 68;
const INDIA_LNG_MAX = 98;
const INDIA_LAT_MIN = 6;
const INDIA_LAT_MAX = 38;

function indiaDegreeHits(ring: number[][]): { lngLat: number; latLng: number } {
  let lngLat = 0;
  let latLng = 0;
  for (const [a, b] of ring) {
    const x = Number(a);
    const y = Number(b);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    if (x >= INDIA_LNG_MIN && x <= INDIA_LNG_MAX && y >= INDIA_LAT_MIN && y <= INDIA_LAT_MAX) {
      lngLat++;
    }
    if (x >= INDIA_LAT_MIN && x <= INDIA_LAT_MAX && y >= INDIA_LNG_MIN && y <= INDIA_LNG_MAX) {
      latLng++;
    }
  }
  return { lngLat, latLng };
}

/** Ring coordinates look like WGS84 geographic degrees (GeoJSON or lat/lng swapped). */
export function looksLikeWgs84Degrees(ring: number[][]): boolean {
  if (!ring.length) return false;
  const { lngLat, latLng } = indiaDegreeHits(ring);
  const minHits = Math.max(3, Math.ceil(ring.length * 0.6));
  return lngLat >= minHits || latLng >= minHits;
}

/** GeoJSON ring stores [latitude, longitude] instead of [longitude, latitude]. */
export function isSwappedLatLngRing(ring: number[][]): boolean {
  const { lngLat, latLng } = indiaDegreeHits(ring);
  return latLng > lngLat;
}

/** Local-metre survey grid footprints (not WGS84 degrees). */
export function looksLikeLocalMetres(ring: number[][]): boolean {
  if (!ring.length) return true;
  if (looksLikeWgs84Degrees(ring)) return false;
  return ring.every(([x, y]) => Math.abs(Number(x)) <= 800 && Math.abs(Number(y)) <= 800);
}

export function extractGeoJsonRing(geojson: unknown): number[][] | null {
  return firstRingFromNormalized(geojson);
}

export type BoundaryCoordMode = 'local' | 'wgs84';

export function detectBoundaryCoordMode(geojson: unknown): BoundaryCoordMode {
  const ring = extractGeoJsonRing(geojson);
  if (!ring?.length) return 'local';
  if (looksLikeWgs84Degrees(ring)) return 'wgs84';
  if (looksLikeLocalMetres(ring)) return 'local';
  return 'wgs84';
}

/** Default georeference origin for local-metre survey grids (Prayagraj PDA). */
export const PDA_SURVEY_ORIGIN: LatLng = { lat: 25.4358, lng: 81.8463 };

function metresPerDegree(originLat: number): { lat: number; lng: number } {
  const latRad = (originLat * Math.PI) / 180;
  return {
    lat: 111_132.954 - 559.822 * Math.cos(2 * latRad),
    lng: 111_132.954 * Math.cos(latRad),
  };
}

/** Local survey metres → WGS84 for map display. */
export function localMetresToWgs84(x: number, y: number, origin: LatLng = PDA_SURVEY_ORIGIN): LatLng {
  const scale = metresPerDegree(origin.lat);
  return {
    lat: origin.lat + Number(y) / scale.lat,
    lng: origin.lng + Number(x) / scale.lng,
  };
}

/** WGS84 map click → local survey metres for storage. */
export function wgs84ToLocalMetres(
  lat: number,
  lng: number,
  origin: LatLng = PDA_SURVEY_ORIGIN
): [number, number] {
  const scale = metresPerDegree(origin.lat);
  return [(lng - origin.lng) * scale.lng, (lat - origin.lat) * scale.lat];
}

/** GeoJSON ring [x,y] or [lng,lat] → Leaflet LatLng on OpenStreetMap. */
export function ringToLeafletLatLngs(ring: number[][], mode: BoundaryCoordMode): LatLng[] {
  if (mode === 'local') {
    return ring.map(([x, y]) => localMetresToWgs84(Number(x), Number(y)));
  }
  if (isSwappedLatLngRing(ring)) {
    return ring.map(([lat, lng]) => ({ lat: Number(lat), lng: Number(lng) }));
  }
  return ring.map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) }));
}

/** Leaflet LatLng[] → closed GeoJSON polygon ring for storage. */
export function latLngsToGeoJsonPolygon(points: LatLng[], mode: BoundaryCoordMode): Record<string, unknown> {
  const ring =
    mode === 'local'
      ? points.map((p) => wgs84ToLocalMetres(p.lat, p.lng))
      : points.map((p) => [p.lng, p.lat]);
  if (ring.length >= 3) {
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
      ring.push([first[0], first[1]]);
    }
  }
  return { type: 'Polygon', coordinates: [ring] };
}

export function computeBoundsFromRings(rings: number[][][], mode: BoundaryCoordMode): LatLng[] | null {
  const points: LatLng[] = [];
  for (const ring of rings) {
    points.push(...ringToLeafletLatLngs(ring, mode));
  }
  if (points.length < 2) return null;
  return points;
}
